import { Field, reduxForm, SubmissionError } from "redux-form";
import { useMemo } from "react";
import { Button, Input, Label } from "expano-components";
import useUpdate from "hooks/useUpdate";
import useGet from "hooks/useGet";
import { Dashboard, InputColorPicker, Select } from "components";

import platform_type_options from "utils/select_options/platform_types";
import available_countries_options from "utils/select_options/available_countries";
import available_currencies_options from "utils/select_options/available_currencies";
import available_languages_options from "utils/select_options/available_languages";

const InputField = (props) => <Input {...props} />;
const InputColorPickerField = (props) => <InputColorPicker {...props} />;
const SelectField = (props) => <Select {...props} />;

const normalizeSourcePlatformId = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const SourcePlatformSelect = ({ input, options, meta, label }) => (
  <Select
    label={label}
    meta={meta}
    isClearable
    options={options}
    value={
      options.find(
        ({ value }) => normalizeSourcePlatformId(value) === normalizeSourcePlatformId(input.value),
      ) || null
    }
    handleChange={(value) => input.onChange(normalizeSourcePlatformId(value))}
  />
);

const BasicData = ({
  platform_id,
  refetch,
  initialValues,
  change,
  submitting,
  handleSubmit,
  pristine,
  initialize,
}) => {
  const { is_updating, update } = useUpdate({
    path: `/platforms/${platform_id}`,
    onSuccess: refetch,
    onError: (err) => {
      throw new SubmissionError(err);
    },
  });

  const onSubmit = async (values) => {
    const payload = {
      ...values,
      source_platform_id: normalizeSourcePlatformId(values.source_platform_id),
    };

    await update(payload);
    initialize(payload);
  };

  const { data: platforms } = useGet({
    query: ["platforms", "source-platform-options"],
    context: "list",
    path: "/platforms",
    search: { per_page: 1000 },
  });

  const source_platform_options = useMemo(() => {
    if (!platforms?.length) return [];

    return platforms
      .map(({ id, name, label_color, label_text }) => ({
        label: (
          <>
            <Label text={label_text} type="bordered" color={label_color} />
            <span>{name}</span>
          </>
        ),
        value: id,
      }));
  }, [platforms]);

  return (
    <Dashboard
      header={{
        icon: "info",
        title: "Podstawowe dane",
      }}
      footer={
        <Button
          type="save"
          text="Zapisz"
          disabled={pristine || submitting || is_updating}
          onClick={handleSubmit(onSubmit)}
        />
      }
    >
      <form
        className="dashboard__form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <Field name="name" label="Nazwa" component={InputField} type="text" />
        <div className="row m-b-10">
          <div className="box-6">
            <Field
              name="label_text"
              label="Skrócona nazwa"
              component={InputField}
              type="text"
            />
          </div>
          <div className="box-6">
            <Field
              name="label_color"
              label="Kolor"
              handleChange={(color) => change("label_color", color)}
              component={InputColorPickerField}
            />
          </div>
        </div>
        <div className="row m-b-10">
          <div className="box-6">
            <Field
              name="identifier"
              label="Identyfikator platformy"
              component={InputField}
              type="text"
            />
          </div>
          <div className="box-6">
            <Field
              label="Typ platformy"
              name="type"
              isDisabled
              component={SelectField}
              options={platform_type_options}
              defaultValue={platform_type_options.find(
                ({ value }) => value === initialValues?.type,
              )}
              handleChange={(value) => {
                change("type", value);
              }}
            />
          </div>
        </div>
        <div className="row m-b-10">
          <div className="box-6">
            <Field
              label="Kraj"
              name="country_code"
              component={SelectField}
              options={available_countries_options}
              defaultValue={available_countries_options.find(
                ({ value }) => value === initialValues?.country_code,
              )}
              handleChange={(value) => {
                change("country_code", value);
              }}
            />
          </div>

          <div className="box-6">
            <Field
              label="Waluta"
              name="currency_code"
              component={SelectField}
              options={available_currencies_options}
              defaultValue={available_currencies_options.find(
                ({ value }) => value === initialValues?.currency_code,
              )}
              handleChange={(value) => {
                change("currency_code", value);
              }}
            />
          </div>
        </div>
        <div className="row m-b-10">
          <div className="box-6">
            <Field
              label="Język"
              name="language"
              component={SelectField}
              options={available_languages_options}
              defaultValue={available_languages_options.find(
                ({ value }) => value === initialValues?.language,
              )}
              handleChange={(value) => {
                change("language", value);
              }}
            />
          </div>
          <div className="box-6">
            <Field
              label="Maks. znaków w tytule oferty"
              name="offer_title_max_length"
              component={InputField}
              type="number"
              min="1"
            />
          </div>
        </div>
        <div className="row m-b-10">
          <div className="box-6">
            <Field
              label="Platforma źródłowa"
              name="source_platform_id"
              component={SourcePlatformSelect}
              options={source_platform_options}
            />
          </div>
        </div>
      </form>
    </Dashboard>
  );
};

export default reduxForm({
  form: "edit-platform",
  enableReinitialize: true,
  keepDirtyOnReinitialize: false,
})(BasicData);
